/**
 * AttendancePolicyDialog — v109.9.125
 *
 * Panchakosh Ventures Attendance Policy & Rules. Rendered inside a
 * scrollable banner dialog with a more creative visual language:
 *   • Hero banner with gradient + at-a-glance ribbon (hours / days / lunch).
 *   • Quick-scan "Golden Rules" chip strip.
 *   • Status codes shown as color-coded chips with rule captions.
 *   • Section cards use asymmetric icon column + numbered eyebrow badges.
 *   • Force-open until acknowledged; button in MyAttendance re-opens.
 */
import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";
import api from "../lib/api";
import {
  ShieldCheck, BookOpen, Calendar, Clock, LogIn, LogOut, ListChecks, AlertTriangle,
  Timer, ClipboardCheck, Sparkles, UserCheck, Info, Ban, CheckCircle2, Loader2,
  Fingerprint, MapPin, Video, Users, TriangleAlert, IndianRupee, ScrollText,
  Sunrise, Sunset, Coffee, Zap, HeartHandshake, Gavel, Scale, Award,
} from "lucide-react";

/* ─────────────────────────────── Primitives ─────────────────────────────── */

const TINTS = {
  indigo:  { grad: "from-indigo-500 to-blue-600",       chip: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300",   ring: "border-indigo-200 dark:border-indigo-800/60"  },
  emerald: { grad: "from-emerald-500 to-teal-600",      chip: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300", ring: "border-emerald-200 dark:border-emerald-800/60" },
  amber:   { grad: "from-amber-500 to-orange-500",      chip: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",       ring: "border-amber-200 dark:border-amber-800/60"    },
  sky:     { grad: "from-sky-500 to-cyan-600",          chip: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",             ring: "border-sky-200 dark:border-sky-800/60"        },
  rose:    { grad: "from-rose-500 to-pink-600",         chip: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",          ring: "border-rose-200 dark:border-rose-800/60"      },
  purple:  { grad: "from-purple-500 to-fuchsia-600",    chip: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",  ring: "border-purple-200 dark:border-purple-800/60"  },
  slate:   { grad: "from-slate-500 to-slate-700",       chip: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",       ring: "border-slate-200 dark:border-slate-700"       },
};

/** A numbered, gradient-ribbon section card. */
const Section = ({ n, icon: Icon, title, tint = "indigo", eyebrow, children }) => {
  const t = TINTS[tint];
  return (
    <section
      className={`relative rounded-2xl border ${t.ring} bg-white/95 dark:bg-slate-900/70 backdrop-blur-sm shadow-sm overflow-hidden`}
      data-testid={`att-policy-section-${n}`}
    >
      {/* Gradient ribbon on the left */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b ${t.grad}`} />
      <div className="pl-5 pr-4 md:pr-5 py-4 md:py-5">
        <header className="flex items-start gap-3 mb-3">
          <span className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${t.grad} text-white shadow-md ring-1 ring-white/40`}>
            <Icon size={18} strokeWidth={2.3} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-[10px] font-bold tracking-widest uppercase text-slate-500 dark:text-slate-400">
              <span className={`inline-block ${t.chip} rounded px-1.5 py-0.5 tabular-nums`}>#{n}</span>
              {eyebrow && <span className="opacity-80">{eyebrow}</span>}
            </div>
            <h2 className="text-base md:text-lg font-bold text-slate-900 dark:text-slate-100 leading-snug mt-0.5">{title}</h2>
          </div>
        </header>
        <div className="space-y-2.5 text-[13px] text-slate-700 dark:text-slate-200 leading-relaxed">
          {children}
        </div>
      </div>
    </section>
  );
};

const Point = ({ icon: Icon = CheckCircle2, tone = "emerald", children }) => {
  const tones = {
    emerald: "text-emerald-600 dark:text-emerald-400",
    rose:    "text-rose-500 dark:text-rose-400",
    amber:   "text-amber-600 dark:text-amber-400",
    sky:     "text-sky-600 dark:text-sky-400",
    indigo:  "text-indigo-600 dark:text-indigo-400",
    slate:   "text-slate-500 dark:text-slate-400",
    purple:  "text-purple-600 dark:text-purple-400",
  };
  return (
    <div className="flex items-start gap-2.5 group">
      <Icon size={15} className={`mt-0.5 shrink-0 ${tones[tone]} transition-transform group-hover:scale-110`} strokeWidth={2.3} />
      <div className="flex-1">{children}</div>
    </div>
  );
};

const Callout = ({ tone = "slate", icon: Icon, title, children }) => {
  const tones = {
    slate:   "bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-700",
    amber:   "bg-amber-50 dark:bg-amber-950/30 border-amber-300 dark:border-amber-800/60",
    rose:    "bg-rose-50 dark:bg-rose-950/30 border-rose-300 dark:border-rose-800/60",
    emerald: "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-800/60",
    sky:     "bg-sky-50 dark:bg-sky-950/30 border-sky-300 dark:border-sky-800/60",
    indigo:  "bg-indigo-50 dark:bg-indigo-950/30 border-indigo-300 dark:border-indigo-800/60",
  };
  return (
    <div className={`rounded-xl border-l-4 border ${tones[tone]} p-3.5`}>
      {title && (
        <div className="flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wide mb-1.5">
          {Icon && <Icon size={14} strokeWidth={2.4} />} {title}
        </div>
      )}
      <div className="space-y-1.5">{children}</div>
    </div>
  );
};

/** Chip strip — for "Golden Rules" quick scan. */
const ChipStrip = ({ items }) => (
  <div className="flex flex-wrap gap-2">
    {items.map((it, i) => {
      const t = TINTS[it.tone || "indigo"];
      const Icon = it.icon || Sparkles;
      return (
        <div
          key={i}
          className={`inline-flex items-center gap-1.5 rounded-full ${t.chip} pl-1 pr-3 py-1 text-[12px] font-semibold shadow-sm border ${t.ring}`}
        >
          <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br ${t.grad} text-white`}>
            <Icon size={12} strokeWidth={2.5} />
          </span>
          {it.label}
        </div>
      );
    })}
  </div>
);

/** Status-code chip. */
const CodeChip = ({ code, tone = "slate" }) => {
  const t = TINTS[tone];
  return (
    <span className={`inline-block font-mono font-black text-[11px] px-2 py-0.5 rounded-md text-white shadow-sm bg-gradient-to-br ${t.grad}`}>
      {code}
    </span>
  );
};

/** A creative status-code "card" (replaces plain table row). */
const StatusRow = ({ code, tone, name, impact, rule, ruleIcon: RuleIcon = Info, impactTone = "slate" }) => (
  <div className="group flex items-center gap-3 rounded-xl bg-white/70 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 px-3 py-2.5 hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-sm transition-all">
    <CodeChip code={code} tone={tone} />
    <div className="flex-1 min-w-0">
      <div className="text-[12.5px] font-bold text-slate-800 dark:text-slate-100 leading-tight">{name}</div>
      <div className={`text-[11px] font-semibold ${TINTS[impactTone].chip.split(" ").slice(1, 3).join(" ")} inline-block px-1.5 py-0.5 rounded mt-0.5`}>
        {impact}
      </div>
    </div>
    <div className="hidden sm:flex items-start gap-1.5 max-w-[45%] text-[11.5px] text-slate-500 dark:text-slate-400 leading-snug">
      <RuleIcon size={13} className="mt-0.5 shrink-0 text-slate-400" strokeWidth={2.2} />
      <span>{rule}</span>
    </div>
  </div>
);

/* ─────────────────────────────── Main dialog ─────────────────────────────── */

export default function AttendancePolicyDialog({ open, onOpenChange, forceOpen = false, onAcknowledged }) {
  const [ack, setAck] = useState({ acknowledged: false, acknowledged_at: null, loading: true });
  const [submitting, setSubmitting] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api.get("/attendance-policy/acknowledgment")
      .then(({ data }) => { if (!cancelled) setAck({ ...data, loading: false }); })
      .catch(() => { if (!cancelled) setAck({ acknowledged: false, acknowledged_at: null, loading: false }); });
    return () => { cancelled = true; };
  }, [open]);

  const acknowledge = async () => {
    if (!checked) { toast.error("Please tick the acknowledgment box first."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/attendance-policy/acknowledgment", {});
      setAck({ ...data, loading: false });
      toast.success("Attendance Policy acknowledged.");
      onAcknowledged?.();
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save acknowledgment.");
    } finally { setSubmitting(false); }
  };

  const handleOpenChange = (v) => {
    if (!v && forceOpen && !ack.acknowledged) return;
    onOpenChange?.(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col p-0 border-0 shadow-2xl"
        data-testid="attendance-policy-dialog"
      >
        {/* ── Hero banner ─────────────────────────────────────────────── */}
        <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-blue-600 to-cyan-600 text-white">
          {/* Subtle grid overlay for depth */}
          <div
            className="absolute inset-0 opacity-[0.15]"
            style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 0)", backgroundSize: "22px 22px" }}
          />
          <div className="relative p-5 md:p-6">
            <DialogHeader>
              <DialogTitle className="text-white">
                <div className="flex items-start gap-4">
                  <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm ring-1 ring-white/30">
                    <BookOpen size={22} strokeWidth={2.2} />
                  </span>
                  <div className="min-w-0">
                    <div className="text-[10.5px] uppercase tracking-[0.22em] opacity-80 font-semibold">Panchakosh Ventures · Group HR Policy</div>
                    <div className="text-xl md:text-2xl font-black leading-tight">Attendance Policy &amp; Rules</div>
                    <div className="mt-1.5 text-[12.5px] opacity-90 font-normal max-w-2xl leading-relaxed">
                      Applies uniformly to Panchakosh Ventures and every group / subsidiary company. Please read carefully, then click <b className="font-semibold">I Acknowledge</b>.
                    </div>
                  </div>
                </div>
              </DialogTitle>
            </DialogHeader>

            {/* At-a-glance ribbon */}
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
              <RibbonStat icon={Sunrise}  label="Start" value="9:30 AM" />
              <RibbonStat icon={Sunset}   label="End"   value="6:30 PM" />
              <RibbonStat icon={Coffee}   label="Lunch" value="30 min" />
              <RibbonStat icon={Calendar} label="Work Week" value="6 Days" />
            </div>
          </div>
        </div>

        {/* ── Scrollable body ─────────────────────────────────────────── */}
        <div className="overflow-y-auto px-4 md:px-6 py-5 space-y-4 bg-slate-50/50 dark:bg-slate-950/30">
          {ack.acknowledged && (
            <Callout tone="emerald" icon={CheckCircle2} title="Acknowledged">
              You acknowledged this policy on {new Date(ack.acknowledged_at).toLocaleString()} · version {ack.version}
            </Callout>
          )}

          {/* Golden Rules chip strip */}
          <Section n="00" tint="purple" icon={Zap} title="Golden Rules — at a glance" eyebrow="Quick Scan">
            <ChipStrip items={[
              { icon: LogIn,        label: "Punch In on arrival",          tone: "emerald" },
              { icon: LogOut,       label: "Punch Out before leaving",     tone: "emerald" },
              { icon: Timer,        label: "≥ 9 hrs = Present (P)",        tone: "sky" },
              { icon: Scale,        label: "4 – 9 hrs = HD-A (Half Pay)",  tone: "amber" },
              { icon: AlertTriangle,label: "< 4 hrs = Absent (A)",         tone: "rose" },
              { icon: ClipboardCheck, label: "Half-day leave needs ≥ 4 hrs work", tone: "sky" },
              { icon: HeartHandshake, label: "Inform Manager if absent",   tone: "indigo" },
              { icon: Gavel,        label: "No proxy / buddy punch",       tone: "rose" },
            ]} />
          </Section>

          {/* 1. Purpose */}
          <Section n="01" icon={Sparkles} title="Why this policy exists" tint="indigo" eyebrow="Purpose">
            <Point icon={Award} tone="indigo">
              Ensures <b>punctuality, discipline, accountability</b> and uninterrupted business operations across every Panchakosh group entity. Every employee is expected to maintain regular attendance and follow the Company&apos;s working-hour procedure.
            </Point>
          </Section>

          {/* 2. Applicability */}
          <Section n="02" icon={Users} title="Who is covered" tint="slate" eyebrow="Applicability">
            <Point icon={Users}>
              Applies to <b>all permanent, probationary, contractual, temporary, interns, trainees, consultants</b> (where applicable), and any other individual engaged by <b>Panchakosh Ventures and its group / subsidiary companies</b>.
            </Point>
          </Section>

          {/* 3. Working Hours */}
          <Section n="03" icon={Clock} title="Working hours &amp; days" tint="sky" eyebrow="Schedule">
            <div className="grid sm:grid-cols-3 gap-2">
              <MiniStat icon={Sunrise} label="Start" value="9:30 AM" tone="sky" />
              <MiniStat icon={Sunset}  label="End"   value="6:30 PM" tone="sky" />
              <MiniStat icon={Timer}   label="Total" value="9 hrs / day" tone="indigo" />
            </div>
            <Point icon={Coffee} tone="sky">8 hrs 30 min productive work + 30 min lunch break.</Point>
            <Point icon={Calendar} tone="sky"><b>Working days:</b> 6 days a week.</Point>
            <Point icon={Timer}>Every employee must complete their prescribed working hours unless otherwise approved.</Point>
          </Section>

          {/* 4. Attendance Marking */}
          <Section n="04" icon={ClipboardCheck} title="How to mark attendance" tint="emerald" eyebrow="Daily Punch">
            <div className="grid sm:grid-cols-2 gap-2">
              <Callout tone="emerald" icon={LogIn} title="Check-In">
                <Point icon={CheckCircle2}>Mark at the beginning of your workday.</Point>
              </Callout>
              <Callout tone="emerald" icon={LogOut} title="Check-Out">
                <Point icon={CheckCircle2}>Mark before leaving the workplace.</Point>
              </Callout>
            </div>
            <Callout tone="amber" icon={AlertTriangle} title="Miss a punch?">
              The day may be treated as <b>Absent (A)</b> unless approved by HR.
            </Callout>
          </Section>

          {/* 5. Attendance Status — creative status-code cards */}
          <Section n="05" icon={ListChecks} title="Attendance status &amp; payroll impact" tint="amber" eyebrow="Codes You'll See">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <StatusRow code="P"      tone="emerald" name="Present"                  impact="Full Pay"                           impactTone="emerald" rule="Worked 9 hrs or more that day"                       ruleIcon={Timer} />
              <StatusRow code="A"      tone="rose"    name="Absent"                   impact="Leave Without Pay"                  impactTone="rose"    rule="No punch in / out OR worked < 4 hrs"                 ruleIcon={AlertTriangle} />
              <StatusRow code="HD-A"   tone="amber"   name="Half-Day Present"         impact="Half Pay (0.5 day worked)"          impactTone="amber"   rule="Worked 4 hrs or more, but less than 9 hrs · no approved leave" ruleIcon={Scale} />
              <StatusRow code="CL"     tone="sky"     name="Casual Leave"             impact="Paid Leave"                         impactTone="sky"     rule="Approved leave · type = Casual · is-half-day = NO"   ruleIcon={ClipboardCheck} />
              <StatusRow code="SL"     tone="sky"     name="Sick Leave"               impact="Paid Leave"                         impactTone="sky"     rule="Approved leave · type = Sick · is-half-day = NO"     ruleIcon={ClipboardCheck} />
              <StatusRow code="EL"     tone="sky"     name="Earned Leave"             impact="Paid Leave"                         impactTone="sky"     rule="Approved leave · type = Earned · is-half-day = NO"   ruleIcon={ClipboardCheck} />
              <StatusRow code="LWP"    tone="rose"    name="Leave Without Pay"        impact="Salary Deduction"                   impactTone="rose"    rule="Approved leave · type = Unpaid · is-half-day = NO"   ruleIcon={IndianRupee} />
              <StatusRow code="HD-CL"  tone="sky"     name="Half-Day Casual"          impact="Paid (0.5 CL deducted)"             impactTone="sky"     rule="Approved half-day Casual leave · worked ≥ 4 hrs"     ruleIcon={ClipboardCheck} />
              <StatusRow code="HD-SL"  tone="sky"     name="Half-Day Sick"            impact="Paid (0.5 SL deducted)"             impactTone="sky"     rule="Approved half-day Sick leave · worked ≥ 4 hrs"       ruleIcon={ClipboardCheck} />
              <StatusRow code="HD-EL"  tone="sky"     name="Half-Day Earned"          impact="Paid (0.5 EL deducted)"             impactTone="sky"     rule="Approved half-day Earned leave · worked ≥ 4 hrs"     ruleIcon={ClipboardCheck} />
              <StatusRow code="HD-LWP" tone="rose"    name="Half-Day LWP"             impact="0.5 Day Salary Deduction"           impactTone="rose"    rule="Approved half-day Unpaid leave · worked ≥ 4 hrs"     ruleIcon={IndianRupee} />
              <StatusRow code="WO"     tone="purple"  name="Weekly Off"               impact="Paid"                               impactTone="purple"  rule="Every Sunday"                                        ruleIcon={Calendar} />
              <StatusRow code="PH"     tone="purple"  name="Public Holiday"           impact="Paid"                               impactTone="purple"  rule="Defined in the Holiday Module"                       ruleIcon={Calendar} />
            </div>
          </Section>

          {/* 6. Late Arrival */}
          <Section n="06" icon={AlertTriangle} title="Late arrival" tint="amber" eyebrow="Punctuality">
            <Point icon={AlertTriangle} tone="amber">Arriving after your scheduled reporting time flags the day as <b>Late</b> in Payroll → Attendance.</Point>
            <Point icon={TriangleAlert} tone="amber">Repeated late reporting may attract disciplinary action.</Point>
            <Point icon={Ban} tone="rose">Habitual late arrivals may be converted to Half-Day / LWP as per management discretion.</Point>
          </Section>

          {/* 7. Early Departure */}
          <Section n="07" icon={LogOut} title="Early departure" tint="rose" eyebrow="Leaving Early">
            <Point>Leaving before completing prescribed hours without approval may be marked as:</Point>
            <div className="grid sm:grid-cols-3 gap-2 pl-1">
              <Point icon={Scale} tone="amber">Half Day</Point>
              <Point icon={Ban} tone="rose">Leave Without Pay</Point>
              <Point icon={TriangleAlert} tone="amber">Unauthorized Absence</Point>
            </div>
          </Section>

          {/* 8. Half-Day */}
          <Section n="08" icon={Scale} title="Half-day rules" tint="sky" eyebrow="Balance & Approval">
            <Point icon={UserCheck} tone="sky">Half-day may be approved only by the Reporting Manager.</Point>
            <Point icon={Scale}>Two half-day leaves = one full-day leave.</Point>
            <div className="text-[12.5px] font-semibold text-slate-600 dark:text-slate-300 mt-1">Adjusted against (in order of preference):</div>
            <div className="grid sm:grid-cols-3 gap-2 pl-1">
              <Point icon={ClipboardCheck} tone="sky">Casual Leave</Point>
              <Point icon={ClipboardCheck} tone="sky">Sick Leave</Point>
              <Point icon={ClipboardCheck} tone="sky">Earned Leave</Point>
            </div>
            <Callout tone="rose" icon={Ban} title="No balance?">
              Treated as <b>Half-Day Leave Without Pay (HD-LWP)</b>.
            </Callout>
          </Section>

          {/* 12. Weekly Off */}
          <Section n="12" icon={Calendar} title="Weekly off &amp; public holidays" tint="emerald" eyebrow="Paid Days">
            <Point icon={CheckCircle2}>Weekly Offs and Company-declared Public Holidays are paid days.</Point>
            <Point icon={Sparkles} tone="purple">Working on such days may qualify for <b>Compensatory Off</b> or an alternate benefit approved by Management.</Point>
          </Section>

          {/* 13. Absence Without Information */}
          <Section n="13" icon={AlertTriangle} title="Absence without information" tint="rose" eyebrow="Communication">
            <Point icon={Info}>Inform your Reporting Manager <b>before the start of the workday</b> or as soon as reasonably possible.</Point>
            <Point icon={Ban} tone="rose">Failure to inform → day marked <b>Absent (A)</b>.</Point>
            <Point icon={TriangleAlert} tone="amber">Continuous unauthorised absence may trigger disciplinary action.</Point>
          </Section>

          {/* 14. Missed Attendance */}
          <Section n="14" icon={ClipboardCheck} title="Missed attendance correction" tint="amber" eyebrow="Fix a Mistake">
            <Point icon={Info}>Forgot to mark? Inform HR and submit a correction request with supporting details at the end of the month when HR publishes the result.</Point>
            <Point icon={Ban} tone="rose">Repeated missed-attendance entries may not be approved.</Point>
          </Section>

          {/* 15. Verification */}
          <Section n="15" icon={ShieldCheck} title="How attendance is verified" tint="slate" eyebrow="Cross-Checks">
            <div className="text-[12.5px] font-semibold text-slate-600 dark:text-slate-300 mb-1">The Company reserves the right to verify through:</div>
            <div className="grid sm:grid-cols-2 gap-2 pl-1">
              <Point icon={Fingerprint} tone="slate">Biometric records</Point>
              <Point icon={MapPin} tone="slate">GPS location</Point>
              <Point icon={ClipboardCheck} tone="slate">Attendance portal</Point>
              <Point icon={MapPin} tone="slate">Travel records</Point>
              <Point icon={Video} tone="slate">CCTV records</Point>
              <Point icon={ScrollText} tone="slate">Official communication records</Point>
              <Point icon={UserCheck} tone="slate">Manager confirmation</Point>
            </div>
            <Callout tone="amber" icon={AlertTriangle} title="Discrepancy?">Any inconsistency may be investigated.</Callout>
          </Section>

          {/* 16. Probation */}
          <Section n="16" icon={UserCheck} title="Attendance during probation" tint="amber" eyebrow="First-90-Days">
            <Point icon={Award} tone="indigo">Excellent attendance is expected during probation.</Point>
            <div className="text-[12.5px] font-semibold text-slate-600 dark:text-slate-300">Irregular attendance may result in:</div>
            <div className="grid sm:grid-cols-2 gap-2 pl-1">
              <Point icon={Timer} tone="amber">Extension of probation</Point>
              <Point icon={IndianRupee} tone="amber">Salary deduction</Point>
              <Point icon={TriangleAlert} tone="amber">Disciplinary action</Point>
              <Point icon={Ban} tone="rose">Termination of employment</Point>
            </div>
          </Section>

          {/* 17. Salary */}
          <Section n="17" icon={IndianRupee} title="How salary is calculated" tint="emerald" eyebrow="Payroll Math">
            <Point icon={CheckCircle2}>Salary is calculated based on approved attendance records.</Point>
            <div className="text-[12.5px] font-semibold text-slate-600 dark:text-slate-300">Deduction happens <b>only</b> for:</div>
            <div className="grid sm:grid-cols-3 gap-2 pl-1">
              <Point icon={Ban} tone="rose">Absent (A)</Point>
              <Point icon={Ban} tone="rose">Leave Without Pay (LWP)</Point>
              <Point icon={Ban} tone="rose">Half-Day LWP (HD-LWP)</Point>
            </div>
            <Point icon={CheckCircle2} tone="emerald">Paid-leave categories do <b>not</b> result in salary deduction.</Point>
          </Section>

          {/* 18. Misuse */}
          <Section n="18" icon={Ban} title="Misuse of attendance" tint="rose" eyebrow="Zero Tolerance">
            <Callout tone="rose" icon={Gavel} title="Serious Misconduct">
              Manipulating records, proxy attendance, buddy punching, false WFH claims, fake travel records, or submitting false information will be treated as serious misconduct.
            </Callout>
            <Point icon={TriangleAlert} tone="rose">May lead to recovery of losses, suspension, or termination.</Point>
          </Section>

          {/* 19. Company's Right */}
          <Section n="19" icon={ScrollText} title="Company's right to amend" tint="slate" eyebrow="Governance">
            <Point icon={Info}>
              Panchakosh Ventures reserves the right to amend, modify, interpret, or withdraw this Attendance Policy at any time based on operational requirements, statutory changes, or business needs across any of its group / subsidiary companies.
            </Point>
          </Section>
        </div>

        {/* ── Footer with acknowledge ─────────────────────────────────── */}
        <DialogFooter className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full">
            <label className="flex items-center gap-2.5 text-[12.5px] font-semibold flex-1 text-slate-700 dark:text-slate-200 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={checked || ack.acknowledged}
                disabled={ack.acknowledged}
                onChange={(e) => setChecked(e.target.checked)}
                data-testid="att-policy-ack-checkbox"
                className="w-4 h-4 accent-indigo-600"
              />
              I have read and understood the Attendance Policy &amp; Rules of Panchakosh Ventures and its group companies.
            </label>
            <Button
              onClick={acknowledge}
              disabled={submitting || ack.loading || ack.acknowledged || !checked}
              className={`min-w-[190px] h-10 text-[13px] font-semibold shadow-lg transition-all ${ack.acknowledged ? "bg-emerald-600 hover:bg-emerald-600" : "bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 hover:shadow-xl"} text-white`}
              data-testid="att-policy-acknowledge-btn"
            >
              {submitting ? (<><Loader2 size={14} className="mr-2 animate-spin" /> Saving…</>)
                : ack.acknowledged ? (<><CheckCircle2 size={14} className="mr-2" /> Acknowledged</>)
                : (<><ShieldCheck size={14} className="mr-2" /> I Acknowledge</>)}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────── Sub-components used only in the hero ────────────────── */

const RibbonStat = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2.5 rounded-xl bg-white/10 border border-white/20 backdrop-blur-sm px-3 py-2">
    <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-white/15">
      <Icon size={15} strokeWidth={2.3} />
    </span>
    <div>
      <div className="text-[10px] uppercase tracking-widest opacity-80 font-semibold">{label}</div>
      <div className="text-[13px] font-bold">{value}</div>
    </div>
  </div>
);

const MiniStat = ({ icon: Icon, label, value, tone = "sky" }) => {
  const t = TINTS[tone];
  return (
    <div className={`rounded-xl border ${t.ring} bg-white dark:bg-slate-900/60 p-3 flex items-center gap-3`}>
      <span className={`inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br ${t.grad} text-white shadow-sm`}>
        <Icon size={15} strokeWidth={2.4} />
      </span>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-semibold">{label}</div>
        <div className="text-[13.5px] font-black text-slate-800 dark:text-slate-100">{value}</div>
      </div>
    </div>
  );
};
